import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    Clock3,
    Download,
    Users,
    AlertTriangle,
    CheckCircle2,
    Building2,
    House,
    TimerReset,
    Search,
    Activity,
    Coffee,
} from "lucide-react";

import api from "../../api/axios";
import { useAuth } from "../../context/AuthContext";
import {
    formatIndiaTime,
    getAutoCheckoutDisplayTime,
    getAutoCheckoutEndMs,
    isAutoCheckoutRecord,
} from "../../utils/attendanceTime";

const TeamAttendance = () => {
    const { employee } = useAuth();
    const currentEmployeeId = String(employee?._id || employee?.id || "");
    const currentEmployeeCode = String(employee?.employeeId || "").toLowerCase();

    const isCurrentEmployee = useCallback((item) => {
        const id = String(item?._id || item?.id || "");
        const employeeCode = String(item?.employeeId || "").toLowerCase();
        return Boolean(
            (currentEmployeeId && id === currentEmployeeId) ||
            (currentEmployeeCode && employeeCode === currentEmployeeCode),
        );
    }, [currentEmployeeCode, currentEmployeeId]);

    const today =
        new Date();

    const past =
        new Date();

    past.setDate(
        today.getDate() -
        30,
    );

    const formatISO =
        (d) =>
            d
                .toISOString()
                .split("T")[0];

    const [records, setRecords] =
        useState([]);
    const [corrections, setCorrections] =
        useState([]);

    const [members, setMembers] =
        useState([]);

    const [loading, setLoading] =
        useState(true);

    const [
        startDate,
        setStartDate,
    ] = useState(
        formatISO(past),
    );

    const [
        endDate,
        setEndDate,
    ] = useState(
        formatISO(today),
    );

    const [
        memberFilter,
        setMemberFilter,
    ] = useState("all");

    const [
        sortKey,
        setSortKey,
    ] = useState("date");

    const [
        search,
        setSearch,
    ] = useState("");

    const [liveTeamFilter, setLiveTeamFilter] =
        useState("all");
    const [liveStatusFilter, setLiveStatusFilter] =
        useState("all");

    // ================= FETCH =================
    const fetchData =
        useCallback(async () => {

            try {

                const [
                    attendanceRes,
                    membersRes,
                    correctionsRes,
                ] =
                    await Promise.all([
                        api.get(
                            "/api/employee/team/attendance",
                        ),

                        api.get(
                            "/api/employee/team/members",
                        ),
                        api.get(
                            "/api/employee/team/attendance-corrections",
                        ).catch(() => ({ data: [] })),
                    ]);

                const nextRecords = Array.isArray(attendanceRes.data)
                    ? attendanceRes.data.filter((record) => !isCurrentEmployee(record.employee))
                    : [];

                const nextMembers = Array.isArray(membersRes.data)
                    ? membersRes.data.filter((member) => !isCurrentEmployee(member))
                    : [];

                setRecords(nextRecords);

                setMembers(nextMembers);

                setCorrections(
                    correctionsRes.data ||
                    [],
                );

            } catch (err) {

                console.error("Failed to load team attendance:", err);

            } finally {

                setLoading(false);
            }
        }, [isCurrentEmployee]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // ================= HELPERS =================
    const formatTime = (
        t,
    ) => {
        return formatIndiaTime(t, "-");
    };

    const formatDate = (
        d,
    ) => {

        if (!d) return "-";

        return new Date(
            d,
        ).toLocaleDateString();
    };

    const formatDurationSeconds = (totalSeconds = 0) => {
        const safeSeconds = Math.max(0, Math.floor(Number(totalSeconds) || 0));
        const hours = Math.floor(safeSeconds / 3600);
        const minutes = Math.floor((safeSeconds % 3600) / 60);
        const seconds = safeSeconds % 60;

        if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
        if (minutes > 0) return `${minutes}m ${seconds}s`;
        return `${seconds}s`;
    };

    const formatDurationMinutes = (minutes = 0) =>
        formatDurationSeconds(Number(minutes || 0) * 60);

    const getWorkDurationMinutes = useCallback((record) => {
        if (!record?.checkIn || !record?.checkOut) {
            return Number(record?.duration || 0);
        }
        const autoCheckout = isAutoCheckoutRecord(record);

        const checkInMs = new Date(record.checkIn).getTime();
        const checkOutMs = autoCheckout
            ? getAutoCheckoutEndMs(record)
            : new Date(record.checkOut).getTime();
        if (Number.isNaN(checkInMs) || Number.isNaN(checkOutMs) || checkOutMs <= checkInMs) {
            return Number(record?.duration || 0);
        }

        const grossSeconds = Math.floor((checkOutMs - checkInMs) / 1000);
        const breakEndMs = record.breakEnd ? new Date(record.breakEnd).getTime() : NaN;
        const isCheckoutClosedLunch = !Number.isNaN(breakEndMs) && Math.abs(breakEndMs - checkOutMs) < 60000;
        const lunchSeconds = Math.min(
            grossSeconds,
            Math.floor(Number(record.breakDuration || 0) * 60),
            isCheckoutClosedLunch ? 45 * 60 : Number.MAX_SAFE_INTEGER,
        );

        return Math.max(0, (grossSeconds - lunchSeconds) / 60);
    }, []);

    const formatWorkDuration = (record) => {
        const minutes = getWorkDurationMinutes(record);
        return minutes ? formatDurationMinutes(minutes) : "-";
    };

    const AutoCheckoutDisplay = ({ time }) => (
        <span className="inline-flex max-w-full flex-wrap items-center gap-1.5">
            <span className="whitespace-nowrap">{time}</span>
            <span className="whitespace-nowrap rounded-full bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-black uppercase leading-none text-amber-700 ring-1 ring-amber-500/20 dark:text-amber-300">
                Auto checked out
            </span>
        </span>
    );

    const formatCheckoutText = (record) => (
        isAutoCheckoutRecord(record) ? `${getAutoCheckoutDisplayTime(record, formatTime)} (Auto checked out)` : formatTime(record?.checkOut)
    );

    const formatCheckout = (record) => (
        isAutoCheckoutRecord(record) ? <AutoCheckoutDisplay time={getAutoCheckoutDisplayTime(record, formatTime)} /> : formatTime(record?.checkOut)
    );

    const isLate = (
        checkIn,
    ) => {

        if (!checkIn)
            return false;

        const t =
            new Date(
                checkIn,
            );

        return (
            t.getHours() >
            9 ||
            (t.getHours() ===
                9 &&
                t.getMinutes() >
                30)
        );
    };

    const formatLunch =
        (mins) => {

            if (!mins)
                return "—";

            if (mins < 60)
                return `${mins}m`;

            const h =
                Math.floor(
                    mins / 60,
                );

            const m =
                mins % 60;

            return `${h}h ${m}m`;
        };

    // ================= FILTER =================
    const filtered =
        useMemo(() => {

            return records
                .filter(
                    (r) => {

                        const d =
                            new Date(
                                r.date,
                            );

                        const start =
                            new Date(
                                startDate,
                            );

                        const end =
                            new Date(
                                endDate,
                            );

                        start.setHours(
                            0,
                            0,
                            0,
                            0,
                        );

                        end.setHours(
                            23,
                            59,
                            59,
                            999,
                        );

                        const inRange =
                            d >=
                            start &&
                            d <=
                            end;

                        const member =
                            memberFilter ===
                            "all" ||
                            r
                                .employee
                                ?._id ===
                            memberFilter;

                        const matchesSearch =
                            r.employee?.name
                                ?.toLowerCase()
                                .includes(
                                    search.toLowerCase(),
                                );

                        return (
                            inRange &&
                            member &&
                            matchesSearch
                        );
                    },
                )
                .sort(
                    (
                        a,
                        b,
                    ) => {

                        if (
                            sortKey ===
                            "hours"
                        ) {
                            return (
                                (b.duration ||
                                    0) -
                                (a.duration ||
                                    0)
                            );
                        }

                        return (
                            new Date(
                                b.date,
                            ) -
                            new Date(
                                a.date,
                            )
                        );
                    },
                );

        }, [
            records,
            startDate,
            endDate,
            memberFilter,
            sortKey,
            search,
        ]);

    // ================= STATS =================
    const stats =
        useMemo(() => {

            return {

                total:
                    filtered.length,

                late:
                    filtered.filter(
                        (
                            r,
                        ) =>
                            isLate(
                                r.checkIn,
                            ),
                    ).length,

                completed:
                    filtered.filter(
                        (
                            r,
                        ) =>
                            r.checkOut,
                    ).length,

                avgHours:
                    filtered.length >
                        0
                        ? (
                            filtered.reduce(
                                (
                                    acc,
                                    cur,
                                ) =>
                                    acc +
                                    getWorkDurationMinutes(cur),
                                0,
                            ) /
                            filtered.length
                        )
                        : 0,
            };

        }, [filtered, getWorkDurationMinutes]);

    const todayRecords = useMemo(() => {
        const key = formatISO(new Date());
        const latest = new Map();
        records
            .filter((record) => String(record.date || "").slice(0, 10) === key)
            .forEach((record) => latest.set(String(record.employee?._id || record.employee), record));
        return latest;
    }, [records]);

    const liveBoard = useMemo(() => members.map((member) => {
        const record = todayRecords.get(String(member._id));
        const isOnBreak = record && ((record.breakStart && !record.breakEnd && !record.checkOut) || (record.breaks || []).some((item) => item.startTime && !item.endTime));
        let status = "Absent";
        let tone = "red";
        if (record?.checkOut) {
            status = "Checked out";
            tone = "green";
        } else if (isOnBreak) {
            status = "Lunch";
            tone = "yellow";
        } else if (record?.checkIn) {
            status = "Working";
            tone = "green";
        }
        return { member, record, status, tone };
    }), [members, todayRecords]);

    const teamOptions = useMemo(() => {
        const teams = Array.from(new Set(members.map((member) => String(member.team || "").trim()).filter(Boolean)));
        return teams.sort((a, b) => a.localeCompare(b));
    }, [members]);

    const liveStatusOptions = ["all", "Working", "Absent", "Lunch", "Checked out"];

    const liveBoardFiltered = useMemo(() => liveBoard.filter(({ member, status }) => {
        const matchesTeam = liveTeamFilter === "all" || String(member.team || "") === liveTeamFilter;
        const matchesStatus = liveStatusFilter === "all" || status === liveStatusFilter;
        return matchesTeam && matchesStatus;
    }), [liveBoard, liveStatusFilter, liveTeamFilter]);

    const handleCorrectionReview = async (request, status) => {
        const leadComment = status === "rejected" ? window.prompt("Reason for rejection:") : window.prompt("Lead comment (optional):");
        if (leadComment === null) return;
        try {
            const res = await api.patch(`/api/employee/team/attendance-corrections/${request._id}`, { status, leadComment });
            setCorrections((current) => current.filter((item) => item._id !== request._id));
            if (res.data?.status === "pending") {
                alert("Approved by lead. Sent to admin for final approval.");
            }
        } catch (err) {
            alert(err.response?.data?.message || "Failed to review correction request.");
        }
    };

    // ================= EXPORT =================
    const exportCSV =
        () => {

            const rows = [
                [
                    "Employee",
                    "Date",
                    "Mode",
                    "CheckIn",
                    "LunchDuration",
                    "LunchStart",
                    "LunchEnd",
                    "CheckOut",
                    "Hours",
                ],

                ...filtered.map(
                    (
                        r,
                    ) => [
                            r
                                .employee
                                ?.name,

                            formatDate(
                                r.date,
                            ),

                            r.mode,

                            formatTime(
                                r.checkIn,
                            ),

                            r.breakDuration
                                ? formatDurationMinutes(r.breakDuration)
                                : "-",

                            formatTime(
                                r.breakStart,
                            ),

                            formatTime(
                                r.breakEnd,
                            ),

                            formatCheckoutText(r),

                            formatWorkDuration(r),
                        ],
                ),
            ];

            const csv =
                "data:text/csv;charset=utf-8," +
                rows
                    .map(
                        (
                            r,
                        ) =>
                            r.join(
                                ",",
                            ),
                    )
                    .join("\n");

            const link =
                document.createElement(
                    "a",
                );

            link.href =
                encodeURI(csv);

            link.download =
                `team_attendance_${Date.now()}.csv`;

            link.click();
        };

    // ================= LOADING =================
    if (loading) {

        return (
            <div
                className="
                min-h-screen

                bg-[var(--bg-primary)]
                text-[var(--text-primary)]

                flex
                items-center
                justify-center
            "
            >
                Loading Team Attendance...
            </div>
        );
    }

    return (
        <div className="team-premium-wrapper text-[var(--text-primary)]">
            <div className="max-w-[1320px] mx-auto px-4 space-y-6">

                {/* ================= HERO ================= */}
                <section className="team-premium-hero">
                    <div className="team-hero-content">
                        <span className="premium-eyebrow text-[#9333ea]">Team Overview</span>
                        <h1>Team Attendance</h1>
                        <p className="mt-2 text-[var(--text-secondary)] font-medium">
                            Monitor team activity, work hours and attendance performance
                        </p>
                    </div>

                    <div className="hidden md:flex w-20 h-20 rounded-3xl bg-gradient-to-br from-[#d946ef] to-[#9333ea] items-center justify-center shadow-lg shadow-[#9333ea]/30">
                        <Users size={34} className="text-white" />
                    </div>
                </section>

                {/* ================= LIVE BOARD ================= */}
                <section className="team-premium-board">
                    <div className="team-board-header flex-col items-start gap-4 lg:flex-row lg:items-center">
                        <div>
                            <p className="text-xs font-bold uppercase tracking-wide text-[#9333ea]">Live status board</p>
                            <h2 className="text-2xl font-bold">Today team status</h2>
                        </div>
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center w-full lg:w-auto">
                            <select
                                value={liveTeamFilter}
                                onChange={(event) => setLiveTeamFilter(event.target.value)}
                                aria-label="Team filter"
                                className="min-h-10 rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 text-sm font-medium text-[var(--text-primary)] focus:border-[#9333ea] focus:ring-2 focus:ring-[#9333ea]/20 outline-none transition-all"
                            >
                                <option value="all">All teams</option>
                                {teamOptions.map((team) => (
                                    <option key={team} value={team}>{team}</option>
                                ))}
                            </select>
                            <div className="flex flex-wrap gap-2">
                                {liveStatusOptions.map((status) => (
                                    <button
                                        key={status}
                                        type="button"
                                        onClick={() => setLiveStatusFilter(status)}
                                        className={`min-h-10 rounded-xl border px-4 py-2 text-xs font-medium transition-all ${liveStatusFilter === status ? "border-transparent bg-[#9333ea] text-white shadow-sm shadow-[#9333ea]/20" : "border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-secondary)] hover:border-[#9333ea]/30"}`}
                                    >
                                        {status === "all" ? "All" : status}
                                    </button>
                                ))}
                            </div>
                            <span className="text-sm font-medium text-[#9333ea] bg-[#9333ea]/10 px-3 py-1.5 rounded-lg whitespace-nowrap">{liveBoardFiltered.length}/{liveBoard.length} members</span>
                        </div>
                    </div>
                    
                    <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {liveBoardFiltered.map(({ member, record, status, tone }) => (
                            <div key={member._id} className="team-live-card">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="min-w-0">
                                        <strong className="block truncate text-base mb-1">{member.name}</strong>
                                        <span className="text-xs text-[var(--text-secondary)] font-semibold">{member.team || "No team"} &bull; {formatTime(record?.checkIn)}</span>
                                    </div>
                                    <Badge color={tone} icon={status === "Lunch" ? <Coffee size={12} /> : status === "Absent" ? <AlertTriangle size={12} /> : <CheckCircle2 size={12} />} label={status} />
                                </div>
                            </div>
                        ))}
                        {!liveBoardFiltered.length && (
                            <p className="rounded-2xl border border-dashed border-[var(--border-color)] bg-[var(--bg-primary)]/50 p-6 text-center text-sm font-bold text-[var(--text-secondary)] sm:col-span-2 lg:col-span-4">
                                No employees match this filter.
                            </p>
                        )}
                    </div>
                </section>

                {/* ================= CORRECTIONS QUEUE ================= */}
                <section className="team-premium-board">
                    <div className="team-board-header mb-6">
                        <div>
                            <p className="text-xs font-bold uppercase tracking-wide text-[#9333ea]">Lead approval queue</p>
                            <h2 className="text-2xl font-bold">Attendance corrections</h2>
                        </div>
                        <span className="text-sm font-bold text-[var(--text-secondary)] bg-[var(--bg-secondary)] px-3 py-1.5 rounded-lg">{corrections.length} waiting</span>
                    </div>
                    
                    <div className="grid gap-4">
                        {corrections.length === 0 ? (
                            <p className="rounded-2xl border border-dashed border-[var(--border-color)] bg-[var(--bg-primary)]/50 p-6 text-center text-sm font-medium text-[var(--text-secondary)]">No correction requests waiting for lead review.</p>
                        ) : corrections.map((request) => (
                            <div key={request._id} className="team-correction-card flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                                <div>
                                    <strong className="text-lg">{request.employee?.name}</strong>
                                    <p className="text-sm text-[var(--text-secondary)] font-medium mt-1 mb-2">
                                        <span className="text-[var(--text-primary)]">{formatDate(request.date)}</span> &bull; {formatTime(request.requestedCheckIn)} - {formatTime(request.requestedCheckOut)} &bull; {request.requestedMode}
                                    </p>
                                    <p className="text-sm bg-[#9333ea]/5 p-3 rounded-xl border border-[#9333ea]/10 text-[var(--text-primary)] italic">"{request.reason}"</p>
                                </div>
                                <div className="flex gap-2 shrink-0">
                                    <button type="button" onClick={() => handleCorrectionReview(request, "approved")} className="rounded-xl bg-emerald-500 hover:bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white transition-colors shadow-lg shadow-emerald-500/20">Approve</button>
                                    <button type="button" onClick={() => handleCorrectionReview(request, "rejected")} className="rounded-xl bg-red-500 hover:bg-red-600 px-5 py-2.5 text-sm font-bold text-white transition-colors shadow-lg shadow-red-500/20">Reject</button>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* ================= STATS ================= */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                    <div className="team-stat-card">
                        <div className="team-stat-icon">
                            <Activity size={24} />
                        </div>
                        <p className="text-sm font-bold uppercase tracking-wider text-[var(--text-secondary)] mb-1">Records</p>
                        <p className="text-3xl font-black text-[var(--text-primary)]">{stats.total}</p>
                    </div>

                    <div className="team-stat-card">
                        <div className="team-stat-icon text-[#e11d48] bg-[#e11d48]/10">
                            <AlertTriangle size={24} />
                        </div>
                        <p className="text-sm font-bold uppercase tracking-wider text-[var(--text-secondary)] mb-1">Late</p>
                        <p className="text-3xl font-black text-[var(--text-primary)]">{stats.late}</p>
                    </div>

                    <div className="team-stat-card">
                        <div className="team-stat-icon text-emerald-600 bg-emerald-600/10">
                            <CheckCircle2 size={24} />
                        </div>
                        <p className="text-sm font-bold uppercase tracking-wider text-[var(--text-secondary)] mb-1">Completed</p>
                        <p className="text-3xl font-black text-[var(--text-primary)]">{stats.completed}</p>
                    </div>

                    <div className="team-stat-card">
                        <div className="team-stat-icon text-blue-600 bg-blue-600/10">
                            <TimerReset size={24} />
                        </div>
                        <p className="text-sm font-bold uppercase tracking-wider text-[var(--text-secondary)] mb-1">Avg Hours</p>
                        <p className="text-3xl font-black text-[var(--text-primary)]">{formatDurationMinutes(stats.avgHours)}</p>
                    </div>
                </div>

                {/* ================= FILTERS ================= */}
                <div
                    className="
                    bg-[var(--card-bg)]

                    border
                    border-[var(--border-color)]

                    shadow-[var(--shadow-soft)]

                    rounded-3xl

                    p-6
                "
                >

                    <div
                        className="
                        grid
                        grid-cols-1
                        md:grid-cols-2
                        xl:grid-cols-6

                        gap-4
                    "
                    >

                        {/* SEARCH */}
                        <div
                            className="
                            xl:col-span-2

                            relative
                        "
                        >

                            <Search
                                size={18}
                                className="
                                absolute

                                left-4
                                top-1/2
                                -translate-y-1/2

                                text-[var(--text-secondary)]
                            "
                            />

                            <input
                                placeholder="Search employee..."
                                value={
                                    search
                                }
                                onChange={(
                                    e,
                                ) =>
                                    setSearch(
                                        e
                                            .target
                                            .value,
                                    )
                                }
                                className="
                                w-full

                                bg-[var(--bg-secondary)]

                                border
                                border-[var(--border-color)]

                                pl-11
                                pr-4
                                py-3

                                rounded-2xl

                                outline-none
                            "
                            />

                        </div>

                        {/* START */}
                        <input
                            type="date"
                            value={
                                startDate
                            }
                            onChange={(
                                e,
                            ) =>
                                setStartDate(
                                    e
                                        .target
                                        .value,
                                )
                            }
                            className="
                            bg-[var(--bg-secondary)]

                            border
                            border-[var(--border-color)]

                            px-4
                            py-3

                            rounded-2xl

                            outline-none
                        "
                        />

                        {/* END */}
                        <input
                            type="date"
                            value={
                                endDate
                            }
                            onChange={(
                                e,
                            ) =>
                                setEndDate(
                                    e
                                        .target
                                        .value,
                                )
                            }
                            className="
                            bg-[var(--bg-secondary)]

                            border
                            border-[var(--border-color)]

                            px-4
                            py-3

                            rounded-2xl

                            outline-none
                        "
                        />

                        {/* MEMBER */}
                        <select
                            value={
                                memberFilter
                            }
                            onChange={(
                                e,
                            ) =>
                                setMemberFilter(
                                    e
                                        .target
                                        .value,
                                )
                            }
                            className="
                            bg-[var(--bg-secondary)]

                            border
                            border-[var(--border-color)]

                            px-4
                            py-3

                            rounded-2xl

                            outline-none
                        "
                        >

                            <option value="all">
                                All Members
                            </option>

                            {members.map(
                                (
                                    m,
                                ) => (
                                    <option
                                        key={
                                            m._id
                                        }
                                        value={
                                            m._id
                                        }
                                    >
                                        {
                                            m.name
                                        }
                                    </option>
                                ),
                            )}

                        </select>

                        {/* SORT */}
                        <select
                            value={
                                sortKey
                            }
                            onChange={(
                                e,
                            ) =>
                                setSortKey(
                                    e
                                        .target
                                        .value,
                                )
                            }
                            className="
                            bg-[var(--bg-secondary)]

                            border
                            border-[var(--border-color)]

                            px-4
                            py-3

                            rounded-2xl

                            outline-none
                        "
                        >

                            <option value="date">
                                Sort by Date
                            </option>

                            <option value="hours">
                                Sort by Hours
                            </option>

                        </select>

                    </div>

                    {/* EXPORT */}
                    <div
                        className="
                        mt-5

                        flex
                        justify-end
                    "
                    >

                        <button
                            onClick={
                                exportCSV
                            }
                            className="
                            inline-flex
                            items-center
                            gap-3

                            bg-[var(--accent-primary)]
                            hover:bg-[var(--accent-hover)]

                            text-white

                            px-5
                            py-3

                            rounded-2xl

                            font-medium

                            transition-all
                        "
                        >

                            <Download
                                size={18}
                            />

                            Export CSV

                        </button>

                    </div>

                </div>

                {/* ================= TABLE ================= */}
                <div
                    className="
                    team-attendance-table-card

                    bg-[var(--card-bg)]

                    border
                    border-[var(--border-color)]

                    shadow-[var(--shadow-soft)]

                    rounded-3xl

                    overflow-hidden
                "
                >

                    <div
                        className="team-attendance-table-scroll"
                    >

                        <table
                            className="
                            w-full
                            min-w-[1000px]
                        "
                        >

                            <thead
                                className="
                                bg-[var(--bg-secondary)]
                            "
                            >

                                <tr>

                                    <Th>
                                        Employee
                                    </Th>

                                    <Th>
                                        Date
                                    </Th>

                                    <Th>
                                        Mode
                                    </Th>

                                    <Th>
                                        Check In
                                    </Th>

                                    <Th>
                                        Lunch Duration
                                    </Th>

                                    <Th>
                                        Lunch Start
                                    </Th>

                                    <Th>
                                        Lunch End
                                    </Th>

                                    <Th>
                                        Check Out
                                    </Th>

                                    <Th>
                                        Hours
                                    </Th>

                                    <Th>
                                        Status
                                    </Th>

                                </tr>

                            </thead>

                            <tbody>

                                {filtered.map(
                                    (
                                        r,
                                        i,
                                    ) => {

                                        const late =
                                            isLate(
                                                r.checkIn,
                                            );

                                        const incomplete =
                                            r.checkIn &&
                                            !r.checkOut;

                                        const isOnBreak =
                                            (r.breaks?.some((b) => b.start && !b.end)) ||
                                            (r.breakStart && !r.breakEnd && !r.checkOut);

                                        const totalBreakMins = (r.breaks || []).reduce(
                                            (sum, b) => sum + (b.duration || 0),
                                            0,
                                        ) || r.breakDuration || 0;

                                        return (
                                            <tr
                                                key={
                                                    i
                                                }
                                                className="
                                                border-t
                                                border-[var(--border-color)]

                                                hover:bg-black/5
                                                dark:hover:bg-white/5

                                                transition-all
                                            "
                                            >

                                                {/* EMPLOYEE */}
                                                <td
                                                    data-label="Employee"
                                                    className="
                                                    px-6
                                                    py-5
                                                "
                                                >

                                                    <div
                                                        className="
                                                        flex
                                                        items-center
                                                        gap-4
                                                    "
                                                    >

                                                        <div
                                                            className="
                                                            w-12
                                                            h-12

                                                            rounded-2xl

                                                            bg-[var(--accent-primary)]

                                                            flex
                                                            items-center
                                                            justify-center

                                                            text-black
                                                            font-bold
                                                        "
                                                        >
                                                            {r.employee?.name
                                                                ?.charAt(
                                                                    0,
                                                                )
                                                                ?.toUpperCase()}
                                                        </div>

                                                        <div>

                                                            <p
                                                                className="
                                                                font-medium
                                                            "
                                                            >
                                                                {
                                                                    r
                                                                        .employee
                                                                        ?.name
                                                                }
                                                            </p>

                                                            <p
                                                                className="
                                                                text-xs

                                                                text-[var(--text-secondary)]
                                                            "
                                                            >
                                                                {
                                                                    r
                                                                        .employee
                                                                        ?.role
                                                                }
                                                            </p>

                                                        </div>

                                                    </div>

                                                </td>

                                                {/* DATE */}
                                                <Td label="Date">
                                                    {formatDate(
                                                        r.date,
                                                    )}
                                                </Td>

                                                {/* MODE */}
                                                <Td label="Mode">

                                                    <div
                                                        className={`
                                                        inline-flex
                                                        items-center
                                                        gap-2

                                                        px-3
                                                        py-1.5

                                                        rounded-full

                                                        text-xs
                                                        text-white

                                                        ${r.mode ===
                                                                "wfh"
                                                                ? "bg-blue-600"
                                                                : "bg-green-600"
                                                            }
                                                    `}
                                                    >

                                                        {r.mode ===
                                                            "wfh" ? (
                                                            <House
                                                                size={
                                                                    14
                                                                }
                                                            />
                                                        ) : (
                                                            <Building2
                                                                size={
                                                                    14
                                                                }
                                                            />
                                                        )}

                                                        {
                                                            r.mode
                                                        }

                                                    </div>

                                                </Td>

                                                {/* IN */}
                                                <Td
                                                    label="Check In"
                                                    className={
                                                        late
                                                            ? "text-red-400 font-medium"
                                                            : ""
                                                    }
                                                >
                                                    {formatTime(
                                                        r.checkIn,
                                                    )}
                                                </Td>

                                                {/* LUNCH DURATION */}
                                                <Td label="Lunch Duration">
                                                    {formatLunch(
                                                        totalBreakMins,
                                                    )}
                                                </Td>

                                                {/* LUNCH START */}
                                                <Td label="Lunch Start">
                                                    {r.breakStart
                                                        ? formatTime(
                                                            r.breakStart,
                                                        )
                                                        : "—"}
                                                </Td>

                                                {/* LUNCH END */}
                                                <Td label="Lunch End">
                                                    {r.breakEnd
                                                        ? formatTime(
                                                            r.breakEnd,
                                                        )
                                                        : isOnBreak
                                                            ? "Running"
                                                            : "—"}
                                                </Td>

                                                {/* OUT */}
                                                <Td label="Check Out">
                                                    {r.checkOut ? formatCheckout(r) : "—"}
                                                </Td>

                                                {/* HOURS */}
                                                <Td label="Hours">
                                                    {formatWorkDuration(r)}
                                                </Td>

                                                {/* STATUS */}
                                                <Td label="Status">

                                                    <div
                                                        className="
                                                        flex
                                                        flex-wrap

                                                        justify-center

                                                        gap-2
                                                    "
                                                    >

                                                        {isOnBreak ? (
                                                            <Badge
                                                                color="yellow"
                                                                icon={
                                                                    <Coffee
                                                                        size={
                                                                            12
                                                                        }
                                                                    />
                                                                }
                                                                label="On Break"
                                                            />
                                                        ) : late ? (
                                                            <Badge
                                                                color="red"
                                                                icon={
                                                                    <AlertTriangle
                                                                        size={
                                                                            12
                                                                        }
                                                                    />
                                                                }
                                                                label="Late"
                                                            />
                                                        ) : incomplete ? (
                                                            <Badge
                                                                color="yellow"
                                                                icon={
                                                                    <Clock3
                                                                        size={
                                                                            12
                                                                        }
                                                                    />
                                                                }
                                                                label="In Progress"
                                                            />
                                                        ) : (
                                                            <Badge
                                                                color="green"
                                                                icon={
                                                                    <CheckCircle2
                                                                        size={
                                                                            12
                                                                        }
                                                                    />
                                                                }
                                                                label="Good"
                                                            />
                                                        )}

                                                    </div>

                                                </Td>

                                            </tr>
                                        );
                                    },
                                )}

                            </tbody>

                        </table>

                    </div>

                </div>

            </div>

        </div>
    );
};

// ================= STAT =================

// ================= TABLE =================
const Th = ({
    children,
}) => (
    <th
        className="
        px-6
        py-5

        text-left

        text-sm
        font-medium

        text-[var(--text-secondary)]
        whitespace-nowrap
    "
    >
        {children}
    </th>
);

const Td = ({
    children,
    className = "",
    label = "",
}) => (
    <td
        data-label={label}
        className={`
        px-6
        py-5

        text-sm
        whitespace-nowrap

        ${className}
    `}
    >
        {children}
    </td>
);

// ================= BADGE =================
const Badge = ({
    color,
    icon,
    label,
}) => {

    const colors = {
        red: "bg-red-500/10 text-red-400 border-red-500/20",
        yellow:
            "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
        green:
            "bg-green-500/10 text-green-400 border-green-500/20",
    };

    return (
        <div
            className={`
            inline-flex
            items-center
            gap-1.5

            px-3
            py-1.5

            rounded-full

            border

            text-xs
            font-medium

            ${colors[color]}
        `}
        >
            {icon}
            {label}
        </div>
    );
};

export default TeamAttendance;
